/**
 * Date ranges for the analytics pages. Mirrors `<yom-report>`'s own
 * `resolveRange` (viewer-local calendar days) so our value cards and the
 * embedded charts always describe the same days.
 */
export type RangePreset = 'today' | '7d' | '30d' | '90d';

export interface DateRange {
  /** YYYY-MM-DD, inclusive. */
  from: string;
  to: string;
}

export const RANGE_PRESETS: { value: RangePreset; label: string; short: string }[] = [
  { value: 'today', label: 'Today', short: 'Today' },
  { value: '7d', label: 'Last 7 days', short: '7 days' },
  { value: '30d', label: 'Last 30 days', short: '30 days' },
  { value: '90d', label: 'Last 90 days', short: '90 days' },
];

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

export function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseLocal(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function isIsoDay(s: string | null | undefined): s is string {
  return Boolean(s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(parseLocal(s).getTime()));
}

export function presetRange(preset: RangePreset, now: Date = new Date()): DateRange {
  const to = localDate(now);
  if (preset === 'today') return { from: to, to };
  const days = Number(preset.replace('d', ''));
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { from: localDate(start), to };
}

export function daysInRange(r: DateRange): number {
  const ms = parseLocal(r.to).getTime() - parseLocal(r.from).getTime();
  return Math.max(1, Math.round(ms / 86_400_000) + 1);
}

/** The same number of days immediately before `r` (what "compare" compares to). */
export function previousRange(r: DateRange): DateRange {
  const days = daysInRange(r);
  const end = parseLocal(r.from);
  end.setDate(end.getDate() - 1);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (days - 1));
  return { from: localDate(start), to: localDate(end) };
}

/** Pick a sensible chart bucket for the range length. */
export function intervalFor(r: DateRange): 'hour' | 'day' | 'week' {
  const days = daysInRange(r);
  if (days <= 2) return 'hour';
  if (days > 62) return 'week';
  return 'day';
}

export function formatRangeLabel(r: DateRange, locale?: string): string {
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  if (r.from === r.to) return fmt.format(parseLocal(r.from));
  return `${fmt.format(parseLocal(r.from))} – ${fmt.format(parseLocal(r.to))}`;
}
